import { useLayoutEffect, useRef, useState } from "react";
import { resolveCommittedNumber } from "@/features/settings/ui/numberDraft";
import { createMediator } from "@/shared/lib/componentHierarchy";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
export type NumberSettingEvent =
  | { type: "number-setting.focus" }
  | { type: "number-setting.input"; value: string }
  | { type: "number-setting.commit"; blur: boolean }
  | { type: "number-setting.sync"; value: number };
export function useNumberSettingPresenter(
  value: number,
  onCommit: (value: number) => void,
  min?: number,
  max?: number,
) {
  const latest = useRef({ value, onCommit, min, max });
  useLayoutEffect(() => {
    latest.current = { value, onCommit, min, max };
  });
  const [mediator] = useState(() =>
    createMediator<
      { draft: string; editing: boolean },
      NumberSettingEvent,
      number
    >(
      { draft: String(value), editing: false },
      (state, event) => {
        switch (event.type) {
          case "number-setting.focus":
            return { status: "handled", state: { ...state, editing: true } };
          case "number-setting.input":
            return {
              status: "handled",
              state: { ...state, draft: event.value },
            };
          case "number-setting.sync":
            return {
              status: "handled",
              state: state.editing
                ? state
                : { ...state, draft: String(event.value) },
            };
          case "number-setting.commit": {
            const input = latest.current;
            const next = resolveCommittedNumber(
              state.draft,
              input.value,
              input.min,
              input.max,
            );
            return {
              status: "handled",
              state: {
                draft: String(next),
                editing: event.blur ? false : state.editing,
              },
              effects: [next],
            };
          }
        }
      },
      { runEffect: (next) => latest.current.onCommit(next) },
    ),
  );
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (!event.type.startsWith("number-setting.")) return "unhandled";
    mediator.dispatch(event as NumberSettingEvent);
    return "handled";
  });
  const state = useMediatorSnapshot(mediator);
  useLayoutEffect(() => {
    if (!state.editing)
      mediator.dispatch({ type: "number-setting.sync", value });
  }, [mediator, value, state.editing]);
  return { node, draft: state.draft };
}
